export type SelectorDimension = "band" | "cup";

export type SelectorDefinition = {
  /** Internal key used for local selection state. */
  key: string;
  /** Which size dimension this dropdown represents. */
  dimension: SelectorDimension;
  /** Translation key for the dropdown placeholder. */
  placeholderKey: string;
  /** Translation key for the "please select" validation message. */
  errorKey: string;
};

const bandSelector: SelectorDefinition = {
  key: "band",
  dimension: "band",
  placeholderKey: "Size_Selector_Overlay_Band_Placeholder",
  errorKey: "Size_Selector_Overlay_Select_Band",
};

const cupSelector: SelectorDefinition = {
  key: "cup",
  dimension: "cup",
  placeholderKey: "Filter_sizechart.sizechart_eu.cup_type",
  errorKey: "Size_Selector_Overlay_Select_Cup",
};

/**
 * Single-size categories (e.g. panties) store their size in the same `.type`
 * facet as the band, so this reuses the band key/dimension but with generic
 * "Size" labels.
 */
const singleSizeSelector: SelectorDefinition = {
  key: "band",
  dimension: "band",
  placeholderKey: "Filter_sizechart.sizechart_eu.type",
  errorKey: "PLP_Select_Size",
};

/**
 * Candidate size dropdowns queried for every category. Both the band (`.type`)
 * and cup (`.cup_type`) facets are requested; the visible selectors are then
 * resolved from whichever facets returned values (see `resolveVisibleSelectors`).
 */
export const sizeSelectors: SelectorDefinition[] = [bandSelector, cupSelector];

/**
 * Resolves which dropdowns to render based on the facet values available for
 * the category and market:
 * - band + cup values (e.g. bra) -> two selectors ("Band size" + "Cup size")
 * - band values only (e.g. panty) -> one generic "Size" selector
 *
 * Values are already market-localized because the facet id is built from the
 * country's active size chart.
 */
export const resolveVisibleSelectors = (
  optionsByKey: Record<string, string[]>,
): SelectorDefinition[] => {
  const hasBand = (optionsByKey.band?.length ?? 0) > 0;
  const hasCup = (optionsByKey.cup?.length ?? 0) > 0;

  if (hasBand && hasCup) {
    return [bandSelector, cupSelector];
  }
  if (hasBand) {
    return [singleSizeSelector];
  }
  return [];
};

/**
 * Builds the Algolia facet id for a size dimension, scoped to the active
 * size chart (country dependent). These match the facet ids the PLP search
 * reads back from `facet_*` URL params.
 */
export const buildSizeFacetId = (
  dimension: SelectorDimension,
  activeSizeChart: string,
): string => {
  if (dimension === "cup") {
    return `sizechart.${activeSizeChart}.cup_type`;
  }
  return `sizechart.${activeSizeChart}.type`;
};
